import { MCP_TOOLS } from "@flowstepdesign/shared";

import { MCP_SERVER_URL } from "./mcpServer";

export type McpClientSlug =
  | "claude-code"
  | "cursor"
  | "claude"
  | "windsurf"
  | "codex";

export type McpClient = {
  slug: McpClientSlug;
  /** Product name, exactly as its makers write it. */
  name: string;
  /** One line for cards and the hub grid. */
  tagline: string;
  /** Where the agent actually runs. Used to differentiate the grid. */
  surface: string;

  /** <title>. Under 60 characters where possible. */
  metaTitle: string;
  metaDescription: string;
  h1: string;
  /**
   * The direct answer, in the first 60 words of the page. Written to stand on
   * its own if a search engine or an assistant quotes only this paragraph.
   */
  lede: string;

  install: {
    /** What the block is: a terminal command, a file, or a URL to paste. */
    label: string;
    language: "shell" | "json" | "text";
    snippet: string;
    /** One line under the block explaining what happens when you run it. */
    caption: string;
    /**
     * Install action shown in the window chrome in place of the Copy button
     * (a deeplink or directory listing that does the setup in one click).
     */
    installUrl?: { href: string; label: string };
  };

  steps: { title: string; body: string }[];
  capabilities: { title: string; body: string }[];

  /** A real exchange, written as it reads in that client. */
  workflow: {
    intro: string;
    turns: { role: "you" | "agent"; text: string }[];
  };

  faqs: { question: string; answer: string }[];
};

// Base64 of {"url":"https://api.flowstep.ai/mcp"}, for Cursor's one-click install
// deeplink. Matches the config written to ~/.cursor/mcp.json below.
const CURSOR_INSTALL_URL =
  "https://cursor.com/install-mcp?name=flowstep&config=eyJ1cmwiOiJodHRwczovL2FwaS5mbG93c3RlcC5haS9tY3AifQ%3D%3D";

export const MCP_CLIENTS: McpClient[] = [
  {
    slug: "claude-code",
    name: "Claude Code",
    tagline: "One command, then design from the terminal you already live in.",
    surface: "Terminal",

    metaTitle: "Flowstep MCP for Claude Code | Design UI From Your Terminal",
    metaDescription:
      "Connect Claude Code to Flowstep with one command. Generate, edit and preview real UI screens from your terminal, then build them in the same session.",
    h1: "Flowstep MCP for Claude Code",
    lede: "Flowstep's MCP server connects Claude Code to your Flowstep account, so the same agent that writes your components can also design them. Add it with one command, sign in through the browser once, and Claude Code can generate screens, render them back as images, and edit them, all without leaving the terminal.",

    install: {
      label: "Terminal",
      language: "shell",
      snippet: `claude mcp add --transport http flowstep ${MCP_SERVER_URL}`,
      caption:
        "Add --scope user to make Flowstep available in every project instead of just this one.",
    },

    steps: [
      {
        title: "Add the server",
        body: "Run the command above in any project. Claude Code registers Flowstep as an HTTP MCP server and stores it in that project's config.",
      },
      {
        title: "Sign in once",
        body: "The first time Claude Code calls a Flowstep tool it opens your browser. Sign in with your Flowstep account using email, password or Google. There is no API key to create, copy or rotate.",
      },
      {
        title: "Check it connected",
        body: "Type /mcp in Claude Code. Flowstep should be listed as connected, with its tools available to the agent.",
      },
      {
        title: "Ask for a screen",
        body: '"Design a settings page for this app and show me the render." Claude Code generates it in Flowstep and returns a PNG inline, so you can react to the design instead of reading a description of it.',
      },
    ],

    capabilities: [
      {
        title: "Design and implement in one session",
        body: `Claude Code already has your repo open. Generate the screen in Flowstep, pull it back with ${MCP_TOOLS.getScreenImage.name}, then write the component against a design it can actually see. No tab switching, no re-describing the layout.`,
      },
      {
        title: "Feed it the context that's already on disk",
        body: "Point Claude Code at the PRD in docs/ or the design system in your repo and have it push that straight into a file's instructions. Every screen generated against that file then follows your rules, without restating them in each prompt.",
      },
      {
        title: "Keep your canvas as the source of truth",
        body: "Screens generated from the terminal land in the same Flowstep file your team is working in, on the same infinite canvas, where they stay editable, shareable, and ready to copy into Figma.",
      },
    ],

    workflow: {
      intro:
        "A typical loop once the server is connected. The whole thing happens in one Claude Code session.",
      turns: [
        {
          role: "you",
          text: "Read docs/billing-prd.md, then design the three screens it describes in Flowstep and show me each render.",
        },
        {
          role: "agent",
          text: `Read the PRD, called ${MCP_TOOLS.aiDesignScreens.name} for the plan picker, payment details and confirmation screens, then ${MCP_TOOLS.getScreenImage.name} on each. Three renders come back inline.`,
        },
        {
          role: "you",
          text: "The plan picker is too dense. Make the cards taller and drop the feature table.",
        },
        {
          role: "agent",
          text: `Called ${MCP_TOOLS.aiDesignScreens.name} on that one screen and returned the updated render. Your canvas in Flowstep updates at the same time.`,
        },
        {
          role: "you",
          text: "Good. Now build the plan picker as a React component using our existing Card and Button.",
        },
      ],
    },

    faqs: [
      {
        question:
          "Do I need an Anthropic API key to use Flowstep from Claude Code?",
        answer:
          "No. The Flowstep MCP server authenticates with your Flowstep account over OAuth. Claude Code opens a browser window the first time it calls a Flowstep tool. Whatever you already use to pay for Claude Code is separate and unchanged.",
      },
      {
        question: "Is Flowstep available in every project or just this one?",
        answer:
          "By default the server is added to the project you ran the command in. Add --scope user to the claude mcp add command to register it once for every project on your machine.",
      },
      {
        question: "How do I check Flowstep is connected?",
        answer:
          "Run /mcp inside Claude Code. It lists every configured MCP server, its connection state and the tools it exposes. If Flowstep shows as needing authentication, run any Flowstep tool and complete the browser sign-in.",
      },
      {
        question: "Does generating from Claude Code use my Flowstep plan?",
        answer: `Yes. Screens generated over MCP count against the same message allowance as screens generated in the Flowstep app. Ask Claude Code to call ${MCP_TOOLS.getPlanDetails.name} to see your remaining quota before a long run.`,
      },
      {
        question: "Can Claude Code see the designs it generates?",
        answer: `Yes. ${MCP_TOOLS.getScreenImage.name} renders any screen to a PNG and returns it inline to your agent if configured, so your agent sees the actual output rather than its own description of it.`,
      },
      {
        question: "How do I remove the server?",
        answer:
          "Run claude mcp remove flowstep. Revoking access on the Flowstep side signs out every connected client at once.",
      },
    ],
  },

  {
    slug: "cursor",
    name: "Cursor",
    tagline:
      "One-click install, then Agent designs and codes in the same repo.",
    surface: "IDE",

    metaTitle: "Flowstep MCP for Cursor | Generate UI Inside Your Editor",
    metaDescription:
      "Add the Flowstep MCP server to Cursor in one click. Ask Cursor Agent to design a screen, preview the render in chat, then build it. No API keys.",
    h1: "Flowstep MCP for Cursor",
    lede: "Flowstep's MCP server gives Cursor Agent a design tool. Install it with one click, sign in with your Flowstep account, and Cursor can generate real UI screens, preview them in the chat panel, and edit them, then write the components in the repo it already has open.",

    install: {
      label: "~/.cursor/mcp.json",
      language: "json",
      snippet: `{
  "mcpServers": {
    "flowstep": {
      "url": "${MCP_SERVER_URL}"
    }
  }
}`,
      caption:
        "Use the install link for a pre-filled setup, or paste this into ~/.cursor/mcp.json for every project, or .cursor/mcp.json for just this one.",
      installUrl: {
        href: CURSOR_INSTALL_URL,
        label: "Add Flowstep to Cursor",
      },
    },

    steps: [
      {
        title: "Install the server",
        body: "Paste the JSON above into ~/.cursor/mcp.json, or into .cursor/mcp.json to keep it scoped to one project. Cursor picks the server up straight from the config.",
      },
      {
        title: "Sign in with Flowstep",
        body: "Cursor prompts you to authenticate the first time it uses a Flowstep tool. Sign in with your Flowstep account in the browser window it opens. Nothing to paste back.",
      },
      {
        title: "Confirm the tools are live",
        body: "Open Cursor's MCP settings. Flowstep should be enabled with its tools listed. If the list is empty, refresh the server there.",
      },
      {
        title: "Design from the chat panel",
        body: "Switch to Agent mode and ask for a screen. The generated design comes back as an image in the chat, next to the code you are about to write.",
      },
    ],

    capabilities: [
      {
        title: "Design where the components already are",
        body: "Cursor knows your codebase. Ask it to design a screen that matches the components you already ship, then have it wire the result up against those same files. The design and the implementation never drift apart, because they came from the same conversation.",
      },
      {
        title: "Skip the wireframe round trip",
        body: "You do not need a designer free, a Figma file open or a blank canvas to start. Describe the screen, look at the render, adjust it in the chat, and move on. The whole loop happens in Agent mode.",
      },
      {
        title: "Import the Figma frame you already have",
        body: `If the design exists in Figma, ask Cursor to import the frame with ${MCP_TOOLS.importFigma.name}. It lands on the Flowstep canvas as editable elements you can expand into the states the original never covered.`,
      },
    ],

    workflow: {
      intro:
        "How the loop reads in Cursor's Agent panel once Flowstep is connected.",
      turns: [
        {
          role: "you",
          text: "Design a team management screen for this app in Flowstep. Match the density of our existing dashboard.",
        },
        {
          role: "agent",
          text: `Read src/components/Dashboard.tsx, called ${MCP_TOOLS.aiDesignScreens.name} with that context, then returned the render inline.`,
        },
        {
          role: "you",
          text: "Add the empty state and the invite-pending state.",
        },
        {
          role: "agent",
          text: `Called ${MCP_TOOLS.aiGenerateScreenVariant.name} twice off the same screen. Both variants land on your Flowstep canvas and come back as images.`,
        },
        {
          role: "you",
          text: "Now implement all three using our Table and Dialog components.",
        },
      ],
    },

    faqs: [
      {
        question: "Where does Cursor store the Flowstep MCP config?",
        answer:
          "In ~/.cursor/mcp.json when you install it globally, or .cursor/mcp.json inside a project when you want it available in that repo only. Both accept the same JSON shown above.",
      },
      {
        question: "Do I need to restart Cursor after installing?",
        answer:
          "No. Cursor picks the server up from the config. If the tool list looks empty, refresh the server from Cursor's MCP settings rather than restarting the editor.",
      },
      {
        question: "How is this different from the Figma MCP server?",
        answer: `Both can generate designs, so it is not a read-versus-write split. Flowstep's output lands on the Flowstep canvas rather than in a Figma file, and from there you can copy it into Figma or export it as code. If the design already exists in Figma, ${MCP_TOOLS.importFigma.name} brings that frame across so you can generate the missing states around it. Running both is fine.`,
      },
      {
        question: "Does it work in Agent mode and Ask mode?",
        answer:
          "Flowstep's tools are called by the agent, so use Agent mode. Ask mode does not run tools.",
      },
      {
        question: "Can Cursor edit a design I made in the Flowstep app?",
        answer: `Yes. ${MCP_TOOLS.listFiles.name} and ${MCP_TOOLS.listScreens.name} show everything in your account, and ${MCP_TOOLS.aiDesignScreens.name} changes screens in place. A screen created in the browser and a screen created from Cursor are the same object on the same canvas.`,
      },
      {
        question: "Is my code sent to Flowstep?",
        answer:
          "Only what you ask the agent to send. Cursor decides what context to include in a tool call, typically the prompt and any file you explicitly pointed it at. Flowstep never pulls from your repo on its own.",
      },
    ],
  },

  {
    slug: "claude",
    name: "Claude",
    tagline:
      "One click in the connector directory, then design without opening an editor.",
    surface: "claude.ai and Claude Desktop",

    metaTitle: "Flowstep MCP for Claude | Design UI in Claude Desktop",
    metaDescription:
      "Add Flowstep to Claude from the connector directory and generate real, editable UI screens from a chat. Works on claude.ai and Claude Desktop. No editor, no API keys.",
    h1: "Flowstep MCP for Claude",
    lede: "Flowstep is a listed connector in Claude's directory, so there is nothing to configure. Add it with one click, sign in with your Flowstep account, and you can generate real, editable UI screens straight from a chat, on claude.ai or in Claude Desktop, with no editor and no API key.",

    install: {
      label: "Connector directory",
      language: "text",
      snippet: "https://claude.ai/directory/flowstep",
      caption:
        "Open the directory listing and click to add — nothing to paste. Works on claude.ai and Claude Desktop.",
      installUrl: {
        href: "https://claude.ai/directory/flowstep",
        label: "Add Flowstep to Claude",
      },
    },

    steps: [
      {
        title: "Add the connector",
        body: "Open Flowstep in Claude's connector directory and click to add it. Flowstep is a listed connector, so there is no custom-connector setup and no URL to paste.",
      },
      {
        title: "Sign in with Flowstep",
        body: "Claude walks you through authorising the connector in the browser. Sign in with your Flowstep account. Connectors are attached to your Claude account, so the same setup follows you between claude.ai and Claude Desktop.",
      },
      {
        title: "Enable Flowstep in a chat",
        body: "Start a conversation and make sure the Flowstep connector is switched on for it. Claude can then call the tools when the conversation calls for a design.",
      },
      {
        title: "Describe the product, not the layout",
        body: '"I\'m building a habit tracker for shift workers. Design the onboarding." Claude generates the screens in Flowstep and shows you the renders in the thread.',
      },
    ],

    capabilities: [
      {
        title: "Think out loud, then see it",
        body: "Most product decisions start as a conversation, not a canvas. Work the problem through with Claude the way you already do, and when the shape is clear, ask for the screens. The thinking and the artefact live in the same thread.",
      },
      {
        title: "No editor required",
        body: "This is the path for people who do not open a terminal. Product managers, founders and marketers get the same generation, editing and Figma export as an engineer running Claude Code, all through a chat window.",
      },
      {
        title: "Attach the doc you were going to send anyway",
        body: "Drop a PRD, a brand guide or a screenshot into the conversation and ask Claude to design against it. Attachments upload straight into Flowstep storage rather than being described back second-hand.",
      },
    ],

    workflow: {
      intro:
        "What the conversation looks like once the connector is switched on.",
      turns: [
        {
          role: "you",
          text: "Here's our brand guide. I need an onboarding flow for a habit tracker aimed at nurses on rotating shifts.",
        },
        {
          role: "agent",
          text: "Uploaded the brand guide as an attachment, saved it to the file's instructions, then generated a four-screen onboarding flow and showed each render.",
        },
        {
          role: "you",
          text: "Screen two asks for too much at once. Split it.",
        },
        {
          role: "agent",
          text: `Called ${MCP_TOOLS.aiDesignScreens.name} on screen two and ${MCP_TOOLS.aiGenerateScreenVariant.name} to add the follow-up step. Both are now on your Flowstep canvas.`,
        },
        {
          role: "you",
          text: "Send the link to my designer. They can pick it up in Flowstep and copy it into Figma.",
        },
      ],
    },

    faqs: [
      {
        question: "Does this work on claude.ai as well as Claude Desktop?",
        answer:
          "Yes. Connectors are configured against your Claude account, so adding Flowstep once makes it available in both the web app and the desktop app.",
      },
      {
        question: "Do I need a paid Claude plan?",
        answer:
          "Connectors are a Claude feature, not a Flowstep one, and availability depends on your Claude plan. Open the Flowstep listing in Claude's connector directory: if you can add it from there, you are set.",
      },
      {
        question: "Do I need a Flowstep subscription?",
        answer:
          "You need a Flowstep account, and the free plan works. Generation over MCP draws on the same message allowance as the Flowstep app, so heavier use means a paid plan.",
      },
      {
        question: "Can I see the designs inside the chat?",
        answer: `Yes. Claude calls ${MCP_TOOLS.getScreenImage.name}, which renders the screen to a PNG and returns it inline in the conversation.`,
      },
      {
        question: "What happens to the screens after the chat ends?",
        answer:
          "They stay in your Flowstep account on the canvas, exactly like screens made in the app. Open them in the browser, keep editing, invite your team, or copy them into Figma.",
      },
      {
        question: "Can I revoke access later?",
        answer:
          "Yes. Remove the connector in Claude's settings, or revoke the session from Flowstep. The connector holds no long-lived API key of its own.",
      },
    ],
  },

  {
    slug: "windsurf",
    name: "Windsurf",
    tagline: "Drop one block into mcp_config.json and Cascade can design.",
    surface: "IDE",

    metaTitle: "Flowstep MCP for Windsurf | Design UI From Cascade",
    metaDescription:
      "Add Flowstep to Windsurf's mcp_config.json and let Cascade generate, preview and edit real UI screens while it works through your codebase. No API keys.",
    h1: "Flowstep MCP for Windsurf",
    lede: "Flowstep's MCP server gives Windsurf's Cascade agent a design tool. Add one block to mcp_config.json, refresh your servers, and sign in with your Flowstep account. Cascade can then generate real UI screens, look at the renders, and edit them as part of the work it is already doing.",

    install: {
      label: "~/.codeium/windsurf/mcp_config.json",
      language: "json",
      snippet: `{
  "mcpServers": {
    "flowstep": {
      "serverUrl": "${MCP_SERVER_URL}"
    }
  }
}`,
      caption:
        "Merge this into the mcpServers object if you already have servers configured, then refresh servers in Windsurf settings.",
    },

    steps: [
      {
        title: "Edit mcp_config.json",
        body: "Open ~/.codeium/windsurf/mcp_config.json and add the flowstep entry. If the file already has an mcpServers object, add the key rather than replacing it.",
      },
      {
        title: "Refresh servers",
        body: "In Windsurf settings, refresh the MCP server list. Flowstep appears with its tools once the connection is made.",
      },
      {
        title: "Sign in with Flowstep",
        body: "The first Flowstep tool call sends you to the browser to sign in. There is no API key to generate and nothing to paste back into the config file.",
      },
      {
        title: "Ask Cascade for a screen",
        body: "Cascade generates it in Flowstep and brings the render back into the conversation, then carries on with the code.",
      },
    ],

    capabilities: [
      {
        title: "Design inside the flow Cascade is already running",
        body: "Cascade works across your project in long, multi-step runs. Adding Flowstep means a run can produce the screen and the implementation together, instead of stopping to wait on a design.",
      },
      {
        title: "One config block, every project",
        body: "mcp_config.json is global to Windsurf, so Flowstep is available in every workspace you open. Nothing to repeat per repo.",
      },
      {
        title: "Pin the output to your design system",
        body: `${MCP_TOOLS.updateFileInstructions.name} stores your product rules, tone and component conventions on the Flowstep file. Every screen Cascade generates against that file follows them, without you restating the system in each prompt.`,
      },
    ],

    workflow: {
      intro: "How it reads in Cascade once the server is refreshed.",
      turns: [
        {
          role: "you",
          text: "Read design-system.md, save it as the instructions on our Flowstep file, then design the notification settings screen.",
        },
        {
          role: "agent",
          text: `Called ${MCP_TOOLS.updateFileInstructions.name} with the file's contents, then ${MCP_TOOLS.aiDesignScreens.name}. The screen comes back on-brand first time.`,
        },
        {
          role: "you",
          text: "Make a dark theme version.",
        },
        {
          role: "agent",
          text: `Called ${MCP_TOOLS.aiEditScreenVariant.name} with the dark theme preset and returned the render.`,
        },
        {
          role: "you",
          text: "Ship it. Build the components and wire them to the settings route.",
        },
      ],
    },

    faqs: [
      {
        question: "Where is Windsurf's MCP config file?",
        answer:
          "~/.codeium/windsurf/mcp_config.json on macOS and Linux. On Windows it sits under your user directory in the same .codeium/windsurf path.",
      },
      {
        question:
          "I already have MCP servers configured. Will this overwrite them?",
        answer:
          "Only if you paste over the whole file. Add the flowstep key inside your existing mcpServers object and the rest keeps working.",
      },
      {
        question: "Windsurf shows no tools after I saved the file",
        answer:
          "Refresh the server list in Windsurf settings. If it still shows nothing, check the JSON is valid, since a trailing comma is the usual cause, and that the key is serverUrl, which is the shape Windsurf expects for a remote server.",
      },
      {
        question: "Does Cascade use my Flowstep plan?",
        answer: `Yes. Generation over MCP counts against the same allowance as the Flowstep app. Ask Cascade to call ${MCP_TOOLS.getPlanDetails.name} to check remaining quota.`,
      },
      {
        question: "Can Cascade edit designs my team made in the browser?",
        answer: `Yes. It is one account and one canvas. ${MCP_TOOLS.listFiles.name} and ${MCP_TOOLS.listScreens.name} surface everything, and ${MCP_TOOLS.aiDesignScreens.name} changes screens in place.`,
      },
    ],
  },
  {
    slug: "codex",
    name: "Codex",
    tagline: "One CLI command, then design from your Codex session.",
    surface: "Terminal",

    metaTitle: "Flowstep MCP for Codex | Design UI From Your Terminal",
    metaDescription:
      "Connect OpenAI Codex to Flowstep with one CLI command. Generate, preview and edit real UI screens from your Codex session. OAuth sign-in, no API keys.",
    h1: "Flowstep MCP for Codex",
    lede: "Flowstep's MCP server gives OpenAI Codex a design tool. Add it with one CLI command, sign in with your Flowstep account through the browser, and Codex can generate real UI screens, look at the renders, and edit them from the same session that is writing your code.",

    install: {
      label: "Terminal",
      language: "shell",
      snippet: `codex mcp add flowstep --url ${MCP_SERVER_URL}`,
      caption:
        "Prefer config files? Add an [mcp_servers.flowstep] block with this url to ~/.codex/config.toml — Codex picks the Streamable HTTP transport automatically when a url is set.",
    },

    steps: [
      {
        title: "Add the server",
        body: "Run the command above, or add the [mcp_servers.flowstep] block to ~/.codex/config.toml to enable Flowstep everywhere. A project-level .codex/config.toml keeps it scoped to trusted projects.",
      },
      {
        title: "Sign in once",
        body: "Run codex mcp login flowstep. Codex opens your browser to sign in with your Flowstep account using email, password or Google. Codex captures the callback and stores the token — there is no API key to create or rotate.",
      },
      {
        title: "Verify the connection",
        body: "codex mcp list shows the server with its OAuth status, and /mcp inside a session lists connected servers. Ask Codex to list your Flowstep files; if the tool call succeeds, you are connected.",
      },
      {
        title: "Ask Codex for a screen",
        body: "Codex generates it in Flowstep and brings the rendered image back into the session, then carries on with the code.",
      },
    ],

    capabilities: [
      {
        title: "Design in the session that builds",
        body: "Codex runs long, multi-step tasks in your repo. With Flowstep connected, the same run can design the screen, look at the render, and implement it — no handoff between tools.",
      },
      {
        title: "OAuth sign-in, no API keys",
        body: "Authentication is a one-time browser sign-in with your Flowstep account. Nothing to generate, paste into config files, or rotate later.",
      },
      {
        title: "Pin the output to your design system",
        body: `${MCP_TOOLS.updateFileInstructions.name} stores your product rules, tone and component conventions on the Flowstep file. Every screen Codex generates against that file follows them, without restating the system in each prompt.`,
      },
    ],

    workflow: {
      intro: "How it reads in a Codex session once the server is added.",
      turns: [
        {
          role: "you",
          text: 'Create a Flowstep file called "Checkout flow", generate a mobile payment screen with a card form and an order summary, then show me the rendered image.',
        },
        {
          role: "agent",
          text: `Called ${MCP_TOOLS.aiDesignScreens.name}, then ${MCP_TOOLS.getScreenImage.name}. Here is the render — the card form sits above a collapsible order summary.`,
        },
        {
          role: "you",
          text: "Looks right. Build the payment screen to match and wire it to the checkout route.",
        },
      ],
    },

    faqs: [
      {
        question: "Where does Codex store the MCP configuration?",
        answer:
          "Globally in ~/.codex/config.toml, or per project in .codex/config.toml for trusted projects. The CLI command writes the global file for you.",
      },
      {
        question: "Do I need to set a transport?",
        answer:
          "No. Codex picks the Streamable HTTP transport automatically when a url is set — no transport field needed.",
      },
      {
        question: "Do I need a Flowstep account first?",
        answer:
          "Yes. Create one at app.flowstep.ai before connecting, then codex mcp login flowstep signs you in through the browser.",
      },
    ],
  },
];

export const getMcpClient = (slug: McpClientSlug): McpClient => {
  const client = MCP_CLIENTS.find((c) => c.slug === slug);
  if (!client) {
    throw new Error(`Unknown MCP client: ${slug}`);
  }
  return client;
};

export const mcpClientPath = (slug: McpClientSlug) => `/mcp/${slug}`;
